import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/joy";
import BugReportIcon from "@mui/icons-material/BugReport";
import PaymentIcon from "@mui/icons-material/Payment";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Link } from "react-router-dom";
import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import { useOrgSettingsContext } from "../providers/OrgSettingsProvider";
import {
  useDebugResetSubscription,
  useGetCustomerPortalUrl,
  useGetOrganizationSubscription,
} from "../services/api";
import { moduleRegistry } from "../modules/moduleRegistry";
import { isDevFeaturesEnabled } from "../utils/devFeatures";
import { describeErrorBody } from "../utils/useAuthFetch";

interface Subscription {
  id: string;
  package: string;
  tier_name: string;
  enabled_modules: string[];
  current_user_count: number;
  max_users: number | null;
  monthly_cost: number;
  base_price: number;
  module_cost: number;
  status: string;
  cancel_at_period_end: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
  updated_at: string | null;
}

const OrgSettingsBilling: React.FC = () => {
  const { user, organizationDetails, loadingOrgDetails } =
    useOrgSettingsContext();
  const getOrganizationSubscription = useGetOrganizationSubscription();
  const getCustomerPortalUrl = useGetCustomerPortalUrl();
  const debugResetSubscription = useDebugResetSubscription();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null,
  );
  const [openingPortal, setOpeningPortal] = useState(false);
  const [resettingSubscription, setResettingSubscription] = useState(false);
  const [billingNotConfigured, setBillingNotConfigured] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);

  useEffect(() => {
    if (!user.organization_id) return;
    const fetchSubscription = async () => {
      setLoadingSubscription(true);
      setSubscriptionError(null);
      try {
        const data = await getOrganizationSubscription(user.organization_id!);
        setSubscription(data.subscription);
      } catch (err: any) {
        if (
          err.message &&
          !err.message.includes("not found") &&
          !err.message.includes("No subscription")
        ) {
          setSubscriptionError(
            err.message || "Failed to load subscription details",
          );
        }
      } finally {
        setLoadingSubscription(false);
      }
    };
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.organization_id]);

  const handleOpenCustomerPortal = async () => {
    if (!user.organization_id) return;
    setOpeningPortal(true);
    try {
      const returnUrl = `${window.location.origin}/`;
      const portalData = await getCustomerPortalUrl(
        user.organization_id,
        returnUrl,
      );
      window.location.href = portalData.customer_portal_url;
    } catch (err: any) {
      const detail = err?.response?.detail;
      if (detail?.code === "billing_not_configured") {
        setBillingNotConfigured(true);
        setBillingError(detail.message);
        setOpeningPortal(false);
        return;
      }
      const errorMessage =
        describeErrorBody(err?.response) ||
        err?.message ||
        "Failed to open billing portal. Please try again.";
      if (
        errorMessage.includes("debug") ||
        errorMessage.includes("test subscription")
      ) {
        alert(
          "Customer portal is not available for debug/test subscriptions. This subscription was created using debug checkout. To use the customer portal, complete a real checkout through Stripe.",
        );
      } else if (
        errorMessage.includes(
          "does not exist in the configured Stripe account",
        ) ||
        errorMessage.includes("No such customer")
      ) {
        alert(
          `${errorMessage}\n\nTo fix this:\n1. Use the "Reset Subscription (Debug)" button below to clear the invalid customer ID\n2. Then create a new subscription using debug checkout with a valid customer ID from your configured Stripe account\n3. Or ensure your ORG_STRIPE_SECRET_KEY matches the Stripe account where the customer exists`,
        );
      } else {
        alert(errorMessage);
      }
      setOpeningPortal(false);
    }
  };

  const handleResetSubscription = async () => {
    if (!user.organization_id) return;
    if (
      !window.confirm(
        "Are you sure you want to reset the subscription? This will delete the current subscription and allow you to do another debug checkout.",
      )
    )
      return;
    setResettingSubscription(true);
    try {
      const result = await debugResetSubscription(user.organization_id);
      alert(
        result.message ||
          "Subscription reset successfully. You can now do another debug checkout.",
      );
      window.location.reload();
    } catch (err: any) {
      alert(
        err?.response?.detail ||
          err?.message ||
          "Failed to reset subscription. Please try again.",
      );
      setResettingSubscription(false);
    }
  };

  if (loadingOrgDetails) {
    return (
      <Page>
        <PageHeader>
          <Typography level="h1">Billing</Typography>
        </PageHeader>
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Loading />
        </Box>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Billing</Typography>
        <Typography level="body-sm" color="neutral">
          Manage your subscription and billing
        </Typography>
      </PageHeader>

      {billingNotConfigured && (
        <Alert color="warning" variant="soft" role="status" sx={{ mb: 2 }}>
          {billingError || "Billing is not configured for this environment."}
        </Alert>
      )}

      {organizationDetails?.status !== "active" ? (
        <Alert variant="soft" color="neutral">
          <Stack spacing={2}>
            <Typography level="body-sm">
              No active subscription found. Please set up billing to activate
              your organization.
            </Typography>
            <Button
              variant="solid"
              color="primary"
              component={Link}
              to="/subscription/select-package"
              startDecorator={<PaymentIcon />}
              size="sm"
              sx={{ alignSelf: "flex-start" }}
            >
              Go to Billing & Subscriptions
            </Button>
          </Stack>
        </Alert>
      ) : loadingSubscription ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Loading />
        </Box>
      ) : subscriptionError ? (
        <Alert color="danger" variant="soft">
          <Typography level="body-sm">{subscriptionError}</Typography>
        </Alert>
      ) : subscription ? (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                  Plan
                </Typography>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography level="title-lg" sx={{ fontWeight: 600 }}>
                    {subscription.package}
                  </Typography>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={
                      subscription.status === "active"
                        ? "success"
                        : subscription.status === "cancelled"
                          ? "danger"
                          : subscription.status === "past_due"
                            ? "warning"
                            : "neutral"
                    }
                  >
                    {subscription.status === "active"
                      ? "Active"
                      : subscription.status === "cancelled"
                        ? "Cancelled"
                        : subscription.status === "past_due"
                          ? "Past Due"
                          : subscription.status}
                  </Chip>
                </Stack>

                {subscription.cancel_at_period_end && (
                  <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                    <Typography level="body-sm">
                      Your subscription will be cancelled at the end of the
                      current billing period.
                    </Typography>
                  </Alert>
                )}

                <Stack spacing={2}>
                  <Box>
                    <Typography
                      level="body-sm"
                      color="neutral"
                      sx={{ mb: 0.5 }}
                    >
                      Monthly Cost
                    </Typography>
                    <Typography level="title-md" sx={{ fontWeight: 600 }}>
                      ${subscription.monthly_cost.toFixed(2)}/month
                    </Typography>
                    {subscription.module_cost > 0 && (
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.secondary", mt: 0.5 }}
                      >
                        Base: ${subscription.base_price.toFixed(2)} + Modules: $
                        {subscription.module_cost.toFixed(2)}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      level="body-sm"
                      color="neutral"
                      sx={{ mb: 0.5 }}
                    >
                      Users
                    </Typography>
                    <Typography level="body-md">
                      {subscription.current_user_count} /{" "}
                      {subscription.max_users === -1
                        ? "Unlimited"
                        : subscription.max_users || "Unlimited"}
                    </Typography>
                  </Box>

                  {subscription.enabled_modules?.length > 0 && (
                    <Box>
                      <Typography
                        level="body-sm"
                        color="neutral"
                        sx={{ mb: 0.5 }}
                      >
                        Enabled Modules
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: "wrap", gap: 1 }}
                      >
                        {subscription.enabled_modules.map((module) => (
                          <Chip
                            key={module}
                            size="sm"
                            variant="soft"
                            color="primary"
                          >
                            {moduleRegistry[module]?.name || module}
                          </Chip>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {subscription.current_period_end && (
                    <Box>
                      <Typography
                        level="body-sm"
                        color="neutral"
                        sx={{ mb: 0.5 }}
                      >
                        {subscription.cancel_at_period_end
                          ? "Ends On"
                          : "Next Billing Date"}
                      </Typography>
                      <Typography level="body-md">
                        {new Date(
                          subscription.current_period_end,
                        ).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Divider />

              <Stack spacing={2}>
                {!billingNotConfigured && (
                  <Button
                    variant="solid"
                    color="primary"
                    onClick={handleOpenCustomerPortal}
                    loading={openingPortal}
                    disabled={openingPortal}
                    startDecorator={<PaymentIcon />}
                    fullWidth
                  >
                    Billing Portal
                  </Button>
                )}
                {!billingNotConfigured && (
                  <Button
                    variant="outlined"
                    color="primary"
                    component={Link}
                    to="/subscription/change-plan"
                    startDecorator={<SwapHorizIcon />}
                    fullWidth
                  >
                    Change Plan
                  </Button>
                )}
                {isDevFeaturesEnabled() && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleResetSubscription}
                    loading={resettingSubscription}
                    disabled={resettingSubscription}
                    startDecorator={<BugReportIcon />}
                    fullWidth
                    title="Dev mode: Reset subscription to allow another debug checkout"
                  >
                    Reset Subscription (Debug)
                  </Button>
                )}
              </Stack>
              <Typography
                level="body-xs"
                sx={{ color: "text.secondary", textAlign: "center" }}
              >
                Update payment methods, view invoices, and manage your
                subscription
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Alert variant="soft" color="neutral">
          <Stack spacing={2}>
            <Typography level="body-sm">
              No active subscription found. Please set up billing to activate
              your organization.
            </Typography>
            <Button
              variant="solid"
              color="primary"
              component={Link}
              to="/subscription/select-package"
              startDecorator={<PaymentIcon />}
              size="sm"
              sx={{ alignSelf: "flex-start" }}
            >
              Go to Billing & Subscriptions
            </Button>
          </Stack>
        </Alert>
      )}
    </Page>
  );
};

export default OrgSettingsBilling;
